import type { MockDatabase, MockRecord } from './database.ts';
import { belongsToPsl, isEligibleVqcaProduct } from './vqcaEligibility.ts';

export interface VqcaSnapshotDetailInput {
  prodSpecId?: unknown;
  oid?: unknown;
  min?: unknown;
  max?: unknown;
  visual?: unknown;
  notes?: unknown;
}

function finiteValue(value: unknown, label: string): number {
  const number = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(number)) throw new Error(`${label} must be a finite number.`);
  return number;
}

function stableHash(value: string): string {
  let hash = 0x811c9dc5;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return `fnv1a-${(hash >>> 0).toString(16).padStart(8, '0')}`;
}

export function validateVqcaHeader(database: MockDatabase, data: Record<string, unknown>): MockRecord {
  const required = ['productId', 'pslId', 'vendorId', 'catMgrId', 'catLevelId'];
  if (required.some((key) => !String(data[key] ?? '').trim())) throw new Error('VQCA product, PSL, vendor, category manager, and level are required.');
  const psl = database.psls?.find((row) => row.oid === data.pslId && row.isActive !== false && row.enabled !== false);
  if (!psl) throw new Error('The selected PSL is not active.');
  const product = database.products?.find((row) => row.oid === data.productId);
  if (!product || !isEligibleVqcaProduct(product, data.pslId)) throw new Error('The product is not active in the selected PSL.');
  if (!database.users?.some((row) => row.oid === data.vendorId || row.userId === data.vendorId)) throw new Error('The selected vendor does not exist.');
  if (!database.users?.some((row) => row.oid === data.catMgrId || row.userId === data.catMgrId)) throw new Error('The selected category manager does not exist.');
  if (!database.categories?.some((row) => row.oid === data.catLevelId && row.isActive !== false && row.enabled !== false)) throw new Error('The selected VQCA level is not active.');
  return product;
}

export function createVqcaSnapshots(database: MockDatabase, data: Record<string, unknown>, issuedAt = new Date().toISOString()): MockRecord[] {
  const product = validateVqcaHeader(database, data);
  if (!Array.isArray(data.details)) throw new Error('VQCA specification details are required.');
  if (data.details.length === 0) throw new Error('A VQCA must contain at least one specification.');
  const seen = new Set<string>();
  const snapshots = (data.details as VqcaSnapshotDetailInput[]).map((detail, index) => {
    const sourceId = String(detail.prodSpecId ?? detail.oid ?? '').trim();
    if (!sourceId || seen.has(sourceId)) throw new Error('VQCA specification details must reference each source specification once.');
    seen.add(sourceId);
    const source = database.specs?.find((row) => row.oid === sourceId || row.prodSpecId === sourceId || row.specId === sourceId);
    if (!source || source.productId !== product.oid || !belongsToPsl(source, data.pslId) || source.isActive === false) {
      throw new Error('A VQCA detail references an inactive, foreign, or unlinked specification.');
    }
    const specTypeName = String(source.specTypeName ?? source.typeName ?? '').toUpperCase();
    const min = detail.min ?? source.min;
    const max = detail.max ?? source.max;
    if (['MIN', 'RANGE', '% OF CONTROL'].includes(specTypeName)) finiteValue(min, 'Specification minimum');
    if (['MAX', 'RANGE', '% OF CONTROL'].includes(specTypeName)) finiteValue(max, 'Specification maximum');
    if (min !== null && min !== undefined && max !== null && max !== undefined && finiteValue(min, 'Specification minimum') > finiteValue(max, 'Specification maximum')) {
      throw new Error('Specification minimum cannot exceed maximum.');
    }
    if (specTypeName === 'VISUAL' && !String(source.visualQ ?? '').trim()) throw new Error('Visual specifications require a question.');
    return {
      sourceSpecId: source.oid, prodSpecId: source.prodSpecId ?? source.oid, productId: product.oid, pslId: data.pslId,
      specTypeName, stpId: source.stpId, stpName: source.stpName, stpNumber: source.stpNumber, stpurl: source.stpurl ?? source.stpURL,
      min: min ?? null, max: max ?? null, visual: detail.visual ?? source.visual ?? false, visualQ: source.visualQ ?? '',
      notes: detail.notes ?? source.specNotes ?? source.notes ?? '', isPerfSpec: source.isPerfSpec === true,
      sortNum: source.sortNum ?? index + 1, snapshotVersion: 1, snapshotIssuedAt: issuedAt, snapshotQuality: 'complete',
    } as unknown as MockRecord;
  });
  if (!snapshots.some((row) => !row.isPerfSpec) || !snapshots.some((row) => row.isPerfSpec)) {
    throw new Error('A VQCA requires both standard and performance specifications.');
  }
  const hash = stableHash(JSON.stringify(snapshots.map(({ sourceSpecId, min, max, visual, notes, snapshotVersion }) => ({ sourceSpecId, min, max, visual, notes, snapshotVersion }))));
  return snapshots.map((row) => ({ ...row, snapshotHash: hash }));
}

export function snapshotIntegrityHash(rows: MockRecord[]): string {
  return stableHash(JSON.stringify(rows.map((row) => ({ sourceSpecId: row.sourceSpecId ?? row.prodSpecId, min: row.min, max: row.max, visual: row.visual, notes: row.notes }))));
}

export function migrateVqcaSnapshots(database: MockDatabase): boolean {
  const migrationId = 'vqca-integrity-snapshots-1';
  if (database.adminMigrations?.some((row) => row.oid === migrationId)) return false;
  for (const agreement of database.vqcas ?? []) {
    const details = (database.vqcaSpecs ?? []).filter((row) => row.vqcaId === agreement.oid);
    const complete = details.length > 0 && details.some((row) => !row.isPerfSpec) && details.some((row) => row.isPerfSpec);
    for (const detail of details) {
      const fields: Record<string, unknown> = {
        sourceSpecId: detail.sourceSpecId ?? detail.prodSpecId ?? detail.oid,
        snapshotVersion: detail.snapshotVersion ?? 1,
        snapshotIssuedAt: detail.snapshotIssuedAt ?? agreement.createdDate,
        snapshotQuality: complete ? 'complete' : 'incomplete-legacy',
      };
      Object.assign(detail, fields);
    }
    const quality = complete ? 'complete' : 'incomplete-legacy';
    const hash = snapshotIntegrityHash(details);
    if (agreement.snapshotQuality !== quality || agreement.snapshotHash !== hash || agreement.snapshotVersion !== 1) {
      Object.assign(agreement, { snapshotVersion: 1, snapshotQuality: quality, snapshotHash: hash, snapshotIssuedAt: agreement.createdDate });
    }
    if (!complete) {
      (database.integrityEvents ??= []).push({ oid: `migration-${agreement.oid}`, vqcaId: agreement.oid, type: 'VQCA_SNAPSHOT_INCOMPLETE', occurredAt: new Date().toISOString(), reason: 'Legacy snapshot could not be fully reconstructed.' });
    }
  }
  (database.adminMigrations ??= []).push({ oid: migrationId, appliedAt: new Date().toISOString() });
  return true;
}